import {
	TEST_STATUS_AUTO_MERGED,
	TEST_STATUS_FLAGGED_FOR_REVIEW,
	TEST_STATUS_PASSED,
} from '../types/TestTypes';

export type RunBaselineIneligibilityCode =
	| 'missing_output'
	| 'unsafe_status'
	| 'recapture_required'
	| 'error_bearing'
	| 'incomplete';

export type RunBaselineEligibility =
	| { eligible: true; code: null; reason: null }
	| { eligible: false; code: RunBaselineIneligibilityCode; reason: string };

export interface RunBaselineCandidate {
	status?: string | null;
	error?: string | null;
	/** The run's actual output as an object (decode GraphQL JSON text before calling). */
	actualOutput?: any;
}

const ACCEPTABLE_RUN_STATUSES = [TEST_STATUS_PASSED, TEST_STATUS_AUTO_MERGED, TEST_STATUS_FLAGGED_FOR_REVIEW];

function ineligible(code: RunBaselineIneligibilityCode, reason: string): RunBaselineEligibility {
	return { eligible: false, code, reason };
}

/**
 * Single authority for whether a test run may become a testcase baseline (Accept). The baseline
 * service enforces it; the review UI uses the same rules to decide whether Accept is offered.
 */
export function getRunBaselineEligibility(run: RunBaselineCandidate | null | undefined): RunBaselineEligibility {
	const actualOutput = run ? run.actualOutput : undefined;
	if (!run
		|| !actualOutput
		|| typeof actualOutput !== 'object'
		|| !Array.isArray(actualOutput.channelWrites)) {
		return ineligible('missing_output', 'Cannot accept snapshot run without actual output');
	}
	if (!ACCEPTABLE_RUN_STATUSES.includes(run.status as string)) {
		return ineligible('unsafe_status', `Cannot accept snapshot run with unsafe status: ${run.status || 'unknown'}`);
	}

	const replayFailure = actualOutput.replayFailure;
	const failureKind = replayFailure?.failureKind;
	const failureProvenance = replayFailure?.provenance;
	const errorText = [run.error, actualOutput.error]
		.filter((value) => typeof value === 'string' && value.trim())
		.join(' ');

	if (failureProvenance === 'source'
		|| failureKind === 'infrastructure_or_capture'
		|| failureKind === 'snapshot_data_incomplete'
		|| replayFailure?.recaptureRequired === true
		|| /(?:snapshot[_ ]data[_ ]incomplete|recapture[_ ]required|source[_ ]infrastructure|definition[_ ]code[_ ]drift)/i.test(errorText)) {
		return ineligible('recapture_required', 'Cannot accept snapshot run that requires source, infrastructure, or snapshot recapture');
	}
	if (actualOutput.failed !== false || errorText || failureKind || replayFailure) {
		return ineligible('error_bearing', 'Cannot accept snapshot run with a failed or error-bearing outcome');
	}
	if (actualOutput.finished !== true) {
		return ineligible('incomplete', 'Cannot accept snapshot run with incomplete output');
	}

	return { eligible: true, code: null, reason: null };
}
